import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Copy, Plus, Trash2, Edit2, Calculator, FileText } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { NodeType } from '@/types/automation';
import {
  useAutomationNodes,
  useCreateAutomationNode,
  useUpdateAutomationNode,
  useDeleteAutomationNode,
} from '@/hooks/useAutomationNodes';
import type { AutomationNode as AutomationNodeModel } from '@/services/automation-nodes.service';

interface ProductAutomationNodesTabProps {
  productId: string;
}

const NODE_TYPE_OPTIONS: { value: NodeType; labelKey: string }[] = [
  { value: 'billing', labelKey: 'products.nodeType.billing' },
  { value: 'report', labelKey: 'products.nodeType.report' },
];

const NODE_TYPE_ICON: Record<NodeType, typeof Calculator> = {
  billing: Calculator,
  report: FileText,
};

export default function ProductAutomationNodesTab({ productId }: ProductAutomationNodesTabProps) {
  const { t } = useTranslation();
  const { data: nodes = [], isLoading, error, refetch } = useAutomationNodes(productId);
  const createNodeMutation = useCreateAutomationNode();
  const updateNodeMutation = useUpdateAutomationNode();
  const deleteNodeMutation = useDeleteAutomationNode();

  const normalizedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        id: node.id,
        name: node.name,
        type: node.type,
        nodeId: node.node_id,
        createdAt: node.created_at,
        raw: node,
      })),
    [nodes]
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newNodeName, setNewNodeName] = useState('');
  const [newNodeType, setNewNodeType] = useState<NodeType>('billing');

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<AutomationNodeModel | null>(null);
  const [editNodeName, setEditNodeName] = useState('');
  const [editNodeType, setEditNodeType] = useState<NodeType>('billing');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);

  const handleAddNode = async () => {
    if (!newNodeName.trim()) return;

    try {
      await createNodeMutation.mutateAsync({
        productId,
        data: {
          name: newNodeName.trim(),
          type: newNodeType,
        },
      });
      setIsAddModalOpen(false);
      setNewNodeName('');
      setNewNodeType('billing');
    } catch (mutationError) {
      // Toast handled inside mutation
    }
  };

  const handleEditNode = (node: AutomationNodeModel) => {
    setEditingNode(node);
    setEditNodeName(node.name);
    setEditNodeType(node.type);
    setIsEditModalOpen(true);
  };

  const handleUpdateNode = async () => {
    if (!editingNode || !editNodeName.trim()) return;

    try {
      await updateNodeMutation.mutateAsync({
        productId,
        nodeId: editingNode.id,
        data: {
          name: editNodeName.trim(),
          type: editNodeType,
        },
      });
      setIsEditModalOpen(false);
      setEditingNode(null);
      setEditNodeName('');
    } catch (mutationError) {
      // Toast handled inside mutation
    }
  };

  const handleDeleteNode = (id: string) => {
    setNodeToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!nodeToDelete) {
      setDeleteConfirmOpen(false);
      return;
    }

    try {
      await deleteNodeMutation.mutateAsync({ productId, nodeId: nodeToDelete });
    } catch (mutationError) {
      // Toast handled inside mutation
    } finally {
      setDeleteConfirmOpen(false);
      setNodeToDelete(null);
    }
  };

  const handleCopyNodeId = (nodeId: string | undefined) => {
    if (!nodeId) return;
    navigator.clipboard.writeText(nodeId);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return '—';
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('products.automationNodesTitle')}</CardTitle>
              <CardDescription>{t('products.generateNodeIds')}</CardDescription>
            </div>
            <Button onClick={() => setIsAddModalOpen(true)} disabled={createNodeMutation.isPending}>
              <Plus className="mr-2 h-4 w-4" />
              {t('products.addNode')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between gap-4">
                <span>{error instanceof Error ? error.message : t('errors.failedToLoadAutomationNodes')}</span>
                <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
                  {t('common.retry')}
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.name')}</TableHead>
                <TableHead>{t('common.type')}</TableHead>
                <TableHead>{t('products.nodeId')}</TableHead>
                <TableHead>{t('products.created')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="ml-auto h-8 w-16" /></TableCell>
                    </TableRow>
                  ))
                : normalizedNodes.length > 0
                ? normalizedNodes.map((node) => {
                    const Icon = NODE_TYPE_ICON[node.type] ?? Calculator;
                    return (
                      <TableRow key={node.id}>
                        <TableCell className="font-medium">{node.name}</TableCell>
                        <TableCell>
                          <Badge variant={node.type === 'billing' ? 'default' : 'secondary'} className="gap-1">
                            <Icon className="h-3 w-3" />
                            {t(`products.nodeType.${node.type}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-muted px-2 py-1 rounded">{node.nodeId || '—'}</code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyNodeId(node.nodeId)}
                              title={t('products.copyNodeId')}
                              className="h-8 w-8"
                              disabled={!node.nodeId}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(node.createdAt)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditNode(node.raw)}
                              title={t('products.editNodeName')}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteNode(node.id)}
                              title={t('products.deleteNode')}
                              disabled={deleteNodeMutation.isPending && nodeToDelete === node.id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                : (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                        {t('products.noAutomationNodes')}
                      </TableCell>
                    </TableRow>
                  )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('products.integrationInstructions')}</CardTitle>
          <CardDescription>{t('products.integrationInstructionsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none dark:prose-invert">
          <ol className="space-y-2">
            <li>{t('products.integrationStep1')}</li>
            <li>{t('products.integrationStep2')}</li>
            <li>{t('products.integrationStep3')}</li>
            <li>{t('products.integrationStep4')}</li>
            <li>{t('products.integrationStep5')}</li>
          </ol>

          <div className="mt-4 rounded-lg bg-muted p-4">
            <p className="text-sm font-medium mb-2">{t('products.important')}</p>
            <p className="text-sm text-muted-foreground">{t('products.automationNodesCalculate')}</p>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={isAddModalOpen}
        onOpenChange={(open) => {
          if (!open && !createNodeMutation.isPending) {
            setNewNodeName('');
            setNewNodeType('billing');
          }
          setIsAddModalOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('products.createAutomationNode')}</DialogTitle>
            <DialogDescription>{t('products.createNodeDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="node-name">{t('products.nodeName')}</Label>
              <Input
                id="node-name"
                placeholder={t('products.nodeNamePlaceholder')}
                value={newNodeName}
                onChange={(event) => setNewNodeName(event.target.value)}
                className="mt-1"
                disabled={createNodeMutation.isPending}
              />
            </div>
            <div>
              <Label htmlFor="node-type">{t('products.nodeTypeLabel')}</Label>
              <Select
                value={newNodeType}
                onValueChange={(value: NodeType) => setNewNodeType(value)}
                disabled={createNodeMutation.isPending}
              >
                <SelectTrigger id="node-type" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NODE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (!createNodeMutation.isPending) {
                  setIsAddModalOpen(false);
                  setNewNodeName('');
                  setNewNodeType('billing');
                }
              }}
              disabled={createNodeMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAddNode} disabled={!newNodeName.trim() || createNodeMutation.isPending}>
              {createNodeMutation.isPending ? t('common.creating') : t('products.createNode')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEditModalOpen}
        onOpenChange={(open) => {
          if (!open && !updateNodeMutation.isPending) {
            setEditingNode(null);
            setEditNodeName('');
          }
          setIsEditModalOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('products.editAutomationNode')}</DialogTitle>
            <DialogDescription>{t('products.editNodeDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>{t('products.nodeId')}</Label>
              <Input value={editingNode?.node_id ?? ''} disabled className="mt-1 font-mono" />
            </div>
            <div>
              <Label htmlFor="edit-node-name">{t('products.nodeName')}</Label>
              <Input
                id="edit-node-name"
                placeholder={t('products.nodeNamePlaceholder')}
                value={editNodeName}
                onChange={(event) => setEditNodeName(event.target.value)}
                className="mt-1"
                disabled={updateNodeMutation.isPending}
              />
            </div>
            <div>
              <Label htmlFor="edit-node-type">{t('products.nodeTypeLabel')}</Label>
              <Select
                value={editNodeType}
                onValueChange={(value: NodeType) => setEditNodeType(value)}
                disabled={updateNodeMutation.isPending}
              >
                <SelectTrigger id="edit-node-type" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NODE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (!updateNodeMutation.isPending) {
                  setIsEditModalOpen(false);
                  setEditingNode(null);
                  setEditNodeName('');
                }
              }}
              disabled={updateNodeMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdateNode} disabled={!editNodeName.trim() || updateNodeMutation.isPending}>
              {updateNodeMutation.isPending ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !deleteNodeMutation.isPending) {
            setNodeToDelete(null);
          }
          setDeleteConfirmOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>{t('common.deleteNodeConfirmation')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteNodeMutation.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleteNodeMutation.isPending}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
